import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, X, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `/api`;

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    company_name: 'HYDROX STUDIOS',
    contact_email: 'contact@hydroxmusic.com',
    service_types: [],
    budget_ranges: [],
    pipeline_stages: [],
    project_statuses: [],
  });
  const [newServiceType, setNewServiceType] = useState('');
  const [newBudgetRange, setNewBudgetRange] = useState('');
  const [newPipelineStage, setNewPipelineStage] = useState('');
  const [newProjectStatus, setNewProjectStatus] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const addServiceType = () => {
    if (newServiceType.trim()) {
      setSettings({
        ...settings,
        service_types: [...settings.service_types, newServiceType.trim()],
      });
      setNewServiceType('');
    }
  };

  const removeServiceType = (index) => {
    setSettings({
      ...settings,
      service_types: settings.service_types.filter((_, i) => i !== index),
    });
  };

  const addBudgetRange = () => {
    if (newBudgetRange.trim()) {
      setSettings({
        ...settings,
        budget_ranges: [...settings.budget_ranges, newBudgetRange.trim()],
      });
      setNewBudgetRange('');
    }
  };

  const removeBudgetRange = (index) => {
    setSettings({
      ...settings,
      budget_ranges: settings.budget_ranges.filter((_, i) => i !== index),
    });
  };

  const addPipelineStage = () => {
    if (newPipelineStage.trim()) {
      setSettings({
        ...settings,
        pipeline_stages: [...settings.pipeline_stages, newPipelineStage.trim()],
      });
      setNewPipelineStage('');
    }
  };

  const removePipelineStage = (index) => {
    setSettings({
      ...settings,
      pipeline_stages: settings.pipeline_stages.filter((_, i) => i !== index),
    });
  };

  const addProjectStatus = () => {
    if (newProjectStatus.trim()) {
      setSettings({
        ...settings,
        project_statuses: [...settings.project_statuses, newProjectStatus.trim()],
      });
      setNewProjectStatus('');
    }
  };

  const removeProjectStatus = (index) => {
    setSettings({
      ...settings,
      project_statuses: settings.project_statuses.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return <div className="text-center py-12 text-zinc-400">Loading settings...</div>;
  }

  return (
    <div className="space-y-8 max-w-4xl w-full">
      <div className="bg-app-card border border-app-border rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Company Information
        </h3>
        <div className="space-y-4">
          <div>
            <Label>Company Name</Label>
            <Input
              data-testid="company-name"
              value={settings.company_name}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              className="bg-zinc-900/50 border-zinc-800 mt-1"
            />
          </div>
          <div>
            <Label>Contact Email</Label>
            <Input
              data-testid="contact-email"
              type="email"
              value={settings.contact_email}
              onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              className="bg-zinc-900/50 border-zinc-800 mt-1"
            />
          </div>
        </div>
      </div>

      <div className="bg-app-card border border-app-border rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Service Types
        </h3>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Add new service type"
              data-testid="new-service-type"
              value={newServiceType}
              onChange={(e) => setNewServiceType(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addServiceType()}
              className="bg-zinc-900/50 border-zinc-800"
            />
            <Button onClick={addServiceType} data-testid="add-service-type" className="bg-blue-500 hover:bg-blue-600">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.service_types.map((type, index) => (
              <div key={index} className="inline-flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-md px-3 py-1.5">
                <span className="text-sm text-zinc-100">{type}</span>
                <button onClick={() => removeServiceType(index)} data-testid={`remove-service-type-${index}`} className="text-app-subtext hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-app-card border border-app-border rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Budget Ranges
        </h3>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Add new budget range"
              data-testid="new-budget-range"
              value={newBudgetRange}
              onChange={(e) => setNewBudgetRange(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addBudgetRange()}
              className="bg-zinc-900/50 border-zinc-800"
            />
            <Button onClick={addBudgetRange} data-testid="add-budget-range" className="bg-blue-500 hover:bg-blue-600">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.budget_ranges.map((range, index) => (
              <div key={index} className="inline-flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-md px-3 py-1.5">
                <span className="text-sm text-zinc-100">{range}</span>
                <button onClick={() => removeBudgetRange(index)} data-testid={`remove-budget-range-${index}`} className="text-app-subtext hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-app-card border border-app-border rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Pipeline Stages
        </h3>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Add new pipeline stage"
              data-testid="new-pipeline-stage"
              value={newPipelineStage}
              onChange={(e) => setNewPipelineStage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addPipelineStage()}
              className="bg-zinc-900/50 border-zinc-800"
            />
            <Button onClick={addPipelineStage} data-testid="add-pipeline-stage" className="bg-blue-500 hover:bg-blue-600">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.pipeline_stages.map((stage, index) => (
              <div key={index} className="inline-flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-md px-3 py-1.5">
                <span className="text-sm text-zinc-100">{stage}</span>
                <button onClick={() => removePipelineStage(index)} data-testid={`remove-pipeline-stage-${index}`} className="text-app-subtext hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-app-card border border-app-border rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Project Statuses
        </h3>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Add new project status"
              data-testid="new-project-status"
              value={newProjectStatus}
              onChange={(e) => setNewProjectStatus(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addProjectStatus()}
              className="bg-zinc-900/50 border-zinc-800"
            />
            <Button onClick={addProjectStatus} data-testid="add-project-status" className="bg-blue-500 hover:bg-blue-600">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.project_statuses.map((status, index) => (
              <div key={index} className="inline-flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-md px-3 py-1.5">
                <span className="text-sm text-zinc-100">{status}</span>
                <button onClick={() => removeProjectStatus(index)} data-testid={`remove-project-status-${index}`} className="text-app-subtext hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} data-testid="save-settings-button" className="bg-blue-500 hover:bg-blue-600 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]">
          <Save className="w-4 h-4 mr-2" />
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default Settings;